// 포인터 소유권만 판단한다. 렌더러에는 window 이벤트로 사용자 회전만 보낸다.
import { isCameraResetCode, keymap } from '../shared/keymap';
import { CAMERA_PREFS_EVENT, loadCameraPrefs, type CameraPrefs } from '../shared/cameraPrefs';
import { Scene, combatScene, type GameView } from '../shared/types';
import { toView } from '../shared/viewport';

export const CAMERA_YAW_EVENT = 'dh-camera-yaw';
export const YAW_PER_PIXEL = Math.PI / 600;

/**
 * 가로 드래그(px) → 카메라 요 변화(rad). 요 +는 카메라가 +x 쪽으로 돌아 시야(바라보는 방향)가 화면 왼쪽으로 돈다.
 * 기본: 손가락을 왼쪽으로 끌면 시야가 왼쪽으로 돈다(요 +). 모바일 액션 RPG의 시점 돌리기 관례이고,
 * 대표님 2026-10-20 보고("왼쪽으로 돌렸는데 카메라가 오른쪽으로 돈다")와 맞다.
 * 예전 기본은 부호가 반대였다(왼쪽으로 끌면 요 −, 시야가 오른쪽으로 돎). invert=true면 그 옛 방향.
 */
export function dragToYaw(dxPx: number, invert: boolean): number {
  const d = -dxPx * YAW_PER_PIXEL;
  return invert ? -d : d;
}

export interface CameraPointer {
  pointerId: number;
  pointerType: string;
  button: number;
  clientX: number;
  /** 세로로 든 기기에서 #app을 돌렸을 때 게임 화면의 가로 = 실제 화면의 세로라 함께 받는다(없으면 0) */
  clientY?: number;
}

/** 게임 화면 기준 가로 위치(돌린 화면이면 실제 화면 y). installCameraGestures가 바꿔 끼운다(시험은 실제 화면 x 그대로) */
type ViewX = (e: CameraPointer) => number;
const rawX: ViewX = (e) => e.clientX;

/**
 * 빈 게임 화면에 닿은 터치만 센다. 조이스틱·버튼·패널 위 손가락은 회전과 잠금 계산에서 빠진다
 * (조이스틱으로 걸으면서 다른 손가락으로 카메라를 돌린다, 2026-10-18 대표님 지시).
 * 빈 화면에 두 번째 손가락이 닿으면(핀치) 빈 화면 손가락을 모두 뗄 때까지 회전을 잠근다(핀치 뒤 한 손가락 점프 방지).
 */
export class CameraGestureState {
  private readonly canvasTouches = new Set<number>();
  private pointer = -1;
  private x = 0;
  private touchOrbit = false;
  private blocked = false;

  constructor(private readonly viewX: ViewX = rawX) {}

  down(e: CameraPointer, blankCanvas: boolean): boolean {
    if (e.pointerType === 'touch') {
      if (!blankCanvas) return false;
      this.canvasTouches.add(e.pointerId);
      if (this.canvasTouches.size > 1) {
        this.blocked = true;
        this.pointer = -1;
      }
      if (this.blocked) return false;
    } else if (!blankCanvas || (e.button !== 1 && e.button !== 2)) return false;
    if (this.pointer !== -1) return false;
    this.pointer = e.pointerId;
    this.x = this.viewX(e);
    this.touchOrbit = e.pointerType === 'touch';
    return true;
  }

  move(e: CameraPointer, invert = false): number {
    if (e.pointerId !== this.pointer || (this.touchOrbit && this.blocked)) return 0;
    const x = this.viewX(e);
    const delta = dragToYaw(x - this.x, invert);
    this.x = x;
    return delta;
  }

  up(e: CameraPointer): void {
    this.canvasTouches.delete(e.pointerId);
    if (e.pointerId === this.pointer) this.pointer = -1;
    if (this.canvasTouches.size === 0) this.blocked = false;
  }

  clear(): void {
    this.canvasTouches.clear();
    this.pointer = -1;
    this.blocked = false;
  }
}

export function installCameraGestures(view: GameView): () => void {
  const canvas = document.getElementById('game');
  if (!canvas) return () => {};
  const state = new CameraGestureState((e) => toView(e.clientX, e.clientY ?? 0).x);
  let dirty = false;
  // 좌우 반전 설정: 이동 이벤트마다 저장소를 읽지 않도록 값을 들고 있다가 설정 이벤트로 갱신한다(터치·마우스 드래그가 같은 경로)
  let invert = loadCameraPrefs().invertYaw;
  const onPrefs = (e: Event): void => { invert = (e as CustomEvent<CameraPrefs>).detail?.invertYaw === true; };
  window.addEventListener(CAMERA_PREFS_EVENT, onPrefs);
  const enabled = (): boolean => (view.flow.scene === Scene.Village || combatScene(view.flow.scene)) &&
    !view.flow.paused && !view.village?.inventoryOpen && !view.village?.stageMapOpen && !view.village?.questOpen && !(view.village?.menuNpc >= 0);
  const save = (): void => {
    if (!dirty) return;
    dirty = false;
    window.dispatchEvent(new CustomEvent(CAMERA_YAW_EVENT, { detail: { save: true } }));
  };
  const down = (e: PointerEvent): void => {
    if (state.down(e, e.target === canvas && enabled())) {
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
    } else save();
  };
  const move = (e: PointerEvent): void => {
    if (!enabled()) { state.clear(); save(); return; }
    const delta = state.move(e, invert);
    if (delta === 0) return;
    dirty = true;
    window.dispatchEvent(new CustomEvent(CAMERA_YAW_EVENT, { detail: { delta } }));
  };
  const up = (e: PointerEvent): void => { state.up(e); save(); };
  const cancel = (): void => { state.clear(); save(); };
  const visibility = (): void => { if (document.hidden) cancel(); };
  const context = (e: Event): void => { e.preventDefault(); };
  const key = (e: KeyboardEvent): void => {
    if (!isCameraResetCode(e.code, keymap.preset) || e.repeat || e.ctrlKey || e.metaKey || e.altKey || !enabled()) return;
    const el = e.target as HTMLElement | null;
    if (el?.isContentEditable || el?.closest('input, textarea, select')) return;
    cancel();
    window.dispatchEvent(new CustomEvent(CAMERA_YAW_EVENT, { detail: { reset: true } }));
    e.preventDefault();
  };
  // 캡처 단계에서 받는다. 조이스틱의 stopPropagation 여부와 무관하게 대상(캔버스인지)만 보고 판단한다.
  window.addEventListener('pointerdown', down, true);
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', up);
  canvas.addEventListener('lostpointercapture', up as EventListener);
  canvas.addEventListener('contextmenu', context);
  window.addEventListener('blur', cancel);
  document.addEventListener('visibilitychange', visibility);
  window.addEventListener('keydown', key);
  return () => {
    cancel();
    window.removeEventListener(CAMERA_PREFS_EVENT, onPrefs);
    window.removeEventListener('pointerdown', down, true);
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', up);
    canvas.removeEventListener('lostpointercapture', up as EventListener);
    canvas.removeEventListener('contextmenu', context);
    window.removeEventListener('blur', cancel);
    document.removeEventListener('visibilitychange', visibility);
    window.removeEventListener('keydown', key);
  };
}
